import { FC, useState, useEffect } from 'react';
import { Button, Dropdown, Form, message, Modal, Popover, Spin, Tooltip } from 'antd';
import * as Yup from 'yup';

//components
import { FloatSelect } from '../Fields/FloatSelect';
import InputSearch from '../Fields/InputSearch';
import { ComponentExpenseArea, ExpenseAreaI } from '../../api/types/expenseareas';
import { useGetExpenseAreaListQuery } from '../../api/expenseAreas';
import { getMenu } from '../../pages/AdminPanel/ManageExpenseAreas/components/getMenu';
import { ChangeStatusModal } from '../../pages/AdminPanel/ManageExpenseAreas/components/ChangeStatusModal';
import { Loader } from '../Loader/Loader';

//api
import { useLazyGetSchoolsQuery } from '../../api/schools';
import { useGetUserDsQuery, useLazyGetUserDsQuery } from '../../api/users';
import { UserDsQueryParams } from '../../api/types/user';
import { useCreateTenderMutation, useUpdateTenderMutation } from '../../api/tenders';
import { CustomErrorType } from '../../api/types/global';

//helpers
import { yupSync } from '../../utils';
import useDebounceSearch from '../../hooks/useDebounceSearch';

//icons
import i from '../../assets/i.svg';
import close from '../../assets/RoundedClose.svg';
import { MoreOutlined } from '@ant-design/icons';
import { ArrowLeft } from '../Icons/ArrowLeft';
import { useNavigate } from 'react-router-dom';
import EllipsisTooltipCell from '../../helpers/EllipsisTooltipCell';

interface ModalProps {
    openModal: boolean;
    setOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
    setTrigger: React.Dispatch<React.SetStateAction<boolean>>;
    messageApi: any;
    type: string;
    data?: any;
}

const validationSchema = Yup.object().shape({
    school: Yup.object().required('This is a required field'),
    school_user: Yup.object().required('This is a required field'),
    tender_representative: Yup.object().required('This is a required field')
});

export const ModalTender: FC<ModalProps> = ({ openModal, setOpenModal, messageApi, type, data, setTrigger }) => {
    const [form] = Form.useForm();
    const navigate = useNavigate();

    const [searchSchools, setSearchSchools] = useState<string>('');
    const [searchUsers, setSearchUsers] = useState<string>('');
    const [searchTenders, setSearchTenders] = useState<string>('');
    const [searchAreas, setSearchAreas] = useState('');
    const debouncedSearch = useDebounceSearch(searchAreas);

    const [selectedEA, setSelectedEA] = useState<number | null>(null);
    const [activePopoverIndex, setActivePopoverIndex] = useState<number | null>(null);
    const [area, setArea] = useState<{ id: number | null; type: string }>({ id: null, type: '' });

    const [school, setSchool] = useState('');

    const [isConfirmModal, setIsConfirmModal] = useState(false);

    const [step, setStep] = useState('general');

    //api
    const [getSchoolsTrigger, { data: dataSchools }] = useLazyGetSchoolsQuery(); // get schools

    const [triggerSchoolUsers, { data: dataUsers }] = useLazyGetUserDsQuery(); // get school users

    const [variables, setVariables] = useState<UserDsQueryParams>({
        filter: { search: undefined },
        per_page: 9999,
        page: 1,
        mode: 'creation-tender-list'
    });
    const { data: dataAreas, refetch } = useGetExpenseAreaListQuery(variables); // get areas

    const [variablesTender, setVariablesTender] = useState<UserDsQueryParams>({
        filter: { search: undefined, role: ['tender_representative'] },
        per_page: -1,
        page: 1
    });

    const { data: dataTender } = useGetUserDsQuery(variablesTender); //get tender users

    const [createTender, { isLoading: createTenderLoading }] = useCreateTenderMutation(); //create tender

    const [updateTender, { isLoading: updatedTenderLoading }] = useUpdateTenderMutation(); // edit tender

    //
    useEffect(() => {
        setVariables({
            ...variables,
            filter: { ...variables.filter, search: debouncedSearch }
        });
    }, [debouncedSearch]);

    useEffect(() => {
        if (!school) return;
        const params = {
            //@ts-ignore
            filter: { search: searchUsers ? searchUsers : undefined, school_id: school?.id, role: ['school_user'] },
            per_page: -1,
            page: 1
        };
        triggerSchoolUsers(params);
    }, [school, searchUsers]);

    useEffect(() => {
        if (searchSchools && searchSchools.length >= 2) {
            const params = {
                //@ts-ignore
                filter: { dropdown_search: searchSchools },
                page: 1
            };
            getSchoolsTrigger(params);
        }
    }, [searchSchools]);

    useEffect(() => {
        setVariablesTender((prev) => ({
            ...prev,
            filter: { ...prev.filter, search: searchTenders }
        }));
    }, [searchTenders]);

    useEffect(() => {
        if (!data) return;
        form.setFieldsValue({
            school: data?.school,
            school_user: data?.school_user,
            tender_representative: data?.tender_representative
        });
        setSelectedEA(data?.expense_area_id);
        setSchool(data?.school);
    }, [data, form]);

    // const editAreaFunc = async (id: number | string, name: string) => {
    //     try {
    //         const data = {
    //             id: id,
    //             data: { name: name?.trim() }
    //         };
    //         await updateArea(data).unwrap();
    //     } catch {
    //     } finally {
    //     }
    // };

    const handleFormSubmit = () => {
        form.validateFields()
            .then(async (values: any) => {
                if (createTenderLoading || updatedTenderLoading) return;

                const baseData = {
                    school_id: values.school.id,
                    su_id: values.school_user.id,
                    tr_id: values.tender_representative.id,
                    ...(step === 'expense_area' && {
                        expense_area_id: selectedEA,
                        step: 'submit'
                    })
                };

                form.setFieldsValue(values);

                const handler = type === 'create' ? createTenderHandler : editTenderHandler;
                handler(baseData);
            })
            .catch(() => {});
    };

    const createTenderHandler = async (baseData: any) => {
        try {
            const res = await createTender(baseData).unwrap();
            handleSuccess(res);
        } catch (error) {
            messageApi.error((error as CustomErrorType)?.data?.message || 'Something went wrong');
        }
    };

    const editTenderHandler = async (baseData: any) => {
        try {
            const res = await updateTender({ data: baseData, id: data.id }).unwrap();
            handleSuccess(res);
        } catch (error) {
            messageApi.error((error as CustomErrorType)?.data?.message || 'Something went wrong');
        }
    };

    const handleSuccess = (res: any) => {
        if (step === 'general') {
            setStep('expense_area');
        } else if (step === 'expense_area') {
            setTrigger((prev) => !prev);
            setOpenModal(false);
            message.success(res?.message);
        }
    };

    const editFunc = (id: number) => {
        return window.open(`/admin-panel/expense_areas?id=${id}&type=edit`, '_blank');
    };

    const dots = (id: number, status: string) => (
        <Dropdown overlay={getMenu(id, status, setArea, editFunc)} trigger={['click']} placement="bottomRight">
            <div
                onClick={(e) => {
                    e.stopPropagation();
                }}
            >
                <MoreOutlined style={{ fontSize: 20, cursor: 'pointer' }} />
            </div>
        </Dropdown>
    );
    return (
        <>
            <Modal
                className="modal-modal"
                destroyOnClose
                centered
                open={openModal}
                onCancel={() => {
                    if (form.isFieldsTouched()) {
                        setIsConfirmModal(true);
                    } else {
                        setOpenModal(false);
                    }
                }}
                closeIcon={<img src={close} />}
                footer={
                    step === 'general' ? (
                        <Button block key="submit" type="primary" onClick={() => handleFormSubmit()}>
                            {type === 'create' ? createTenderLoading ? <Loader /> : 'Create' : ''}
                            {type === 'edit' ? updatedTenderLoading ? <Loader /> : 'Update' : ''}
                        </Button>
                    ) : (
                        <div style={{ display: 'flex', justifyItems: 'space-between', gap: '16px', width: '100%' }}>
                            <Button
                                block
                                key="back"
                                onClick={() => {
                                    setStep('general');
                                }}
                                className="cancel"
                            >
                                <div className="flex">
                                    <ArrowLeft fill={'#001965'} />
                                    <span style={{ margin: '0 0 0 5px' }}>Previous</span>
                                </div>
                            </Button>
                            <Tooltip title={selectedEA ? '' : 'Please select Expense Area first'}>
                                <Button
                                    block
                                    key="submit"
                                    type="primary"
                                    disabled={!selectedEA}
                                    onClick={() => handleFormSubmit()}
                                >
                                    {type === 'create' ? createTenderLoading ? <Loader /> : 'Create' : ''}
                                    {type === 'edit' ? updatedTenderLoading ? <Loader /> : 'Update' : ''}
                                </Button>
                            </Tooltip>
                        </div>
                    )
                }
            >
                <Spin spinning={createTenderLoading || updatedTenderLoading} size="large">
                    <div className="modal">
                        <div className="modal__header" style={{ height: '102px' }}>
                            <div className="modal__title">
                                {type === 'create' ? 'Create Matchmaking Journey' : 'Edit Matchmaking Journey'}
                            </div>
                            {data?.number ? <div className="modal__id">ID {data?.number}</div> : ''}
                        </div>

                        <div className="modal__main main createTender" style={{ padding: '24px 56px 43px 56px' }}>
                            <div className={`createTender__steps ${step === 'expense_area' ? 'active' : ''}`}>
                                <div className={`createTender__step active ${step === 'expense_area' ? 'done' : ''}`}>
                                    <div className="createTender__number">
                                        {step === 'expense_area' ? (
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="20"
                                                height="20"
                                                viewBox="0 0 20 20"
                                                fill="none"
                                            >
                                                <path
                                                    d="M16.6673 5.41309L7.50065 14.5798L3.33398 10.4131"
                                                    stroke="white"
                                                    stroke-width="1.5"
                                                    stroke-linecap="round"
                                                    stroke-linejoin="round"
                                                />
                                            </svg>
                                        ) : (
                                            '1'
                                        )}
                                    </div>{' '}
                                    General Info
                                </div>{' '}
                                - - - - - - - - - - - - - - - - - - - - - - -
                                <div className={`createTender__step ${step === 'expense_area' ? 'active' : ''}`}>
                                    <div className="createTender__number">2</div> Expense Area
                                </div>
                            </div>

                            <Form form={form}>
                                <div style={{ display: step === 'general' ? 'block' : 'none' }}>
                                    {' '}
                                    <div className="main__subtitle" style={{ margin: '0 0 16px 0' }}>
                                        All fields are mandatory
                                    </div>
                                    <Form.Item
                                        className="input-wrapper"
                                        name="school"
                                        rules={yupSync('school', validationSchema, true)}
                                    >
                                        <FloatSelect
                                            search={searchSchools}
                                            floatLabel="Organisation"
                                            className="input-select"
                                            required={false}
                                            err={false}
                                            setSearch={setSearchSchools}
                                            options={dataSchools?.data}
                                            onChange={setSchool}
                                            value={school}
                                            form={form}
                                            hasError={() => !!form.getFieldError(['school']).length}
                                            disabled={type === 'edit' ? true : false}
                                        />
                                    </Form.Item>
                                    <Form.Item
                                        className="input-wrapper"
                                        name="school_user"
                                        rules={yupSync('school_user', validationSchema, true)}
                                    >
                                        <FloatSelect
                                            type={'user'}
                                            search={searchUsers}
                                            floatLabel="Buyer"
                                            className="input-select"
                                            required={false}
                                            err={false}
                                            setSearch={setSearchUsers}
                                            options={dataUsers?.data}
                                            onChange={() => {}}
                                            value={''}
                                            form={form}
                                            disabled={school ? false : true}
                                            hasError={() => !!form.getFieldError(['school_user']).length}
                                        />
                                    </Form.Item>
                                    <Form.Item
                                        className="input-wrapper"
                                        name="tender_representative"
                                        rules={yupSync('tender_representative', validationSchema, true)}
                                    >
                                        <FloatSelect
                                            type={'tender'}
                                            search={searchTenders}
                                            floatLabel="Tender Expert"
                                            className="input-select"
                                            required={false}
                                            err={false}
                                            setSearch={setSearchTenders}
                                            options={dataTender?.data}
                                            onChange={() => {}}
                                            value={''}
                                            form={form}
                                            hasError={() => !!form.getFieldError(['tender_representative']).length}
                                        />
                                    </Form.Item>
                                </div>

                                <div style={{ display: step === 'expense_area' ? 'block' : 'none' }}>
                                    <div className="note" style={{ margin: '0 0 15px 0' }}>
                                        <div>
                                            <div className="note__row">
                                                <img src={i} />
                                                <div>
                                                    To update or add a new expense area, please go to the{' '}
                                                    <b>'Expense Areas'</b> section in the admin panel.
                                                </div>
                                            </div>
                                            <br />
                                            <div className="note__row">
                                                <div>
                                                    <b>Manage Expense Areas</b>
                                                </div>
                                                <div
                                                    style={{ transform: 'rotate(180deg)' }}
                                                    onClick={() => navigate('/admin-panel/expense_areas')}
                                                >
                                                    <ArrowLeft fill="#001965" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="main__subtitle">Select Expense Area</div>
                                    <InputSearch handleChange={setSearchAreas} placeholder="Search Expense Area" />
                                    <div className="createTender__areas">
                                        {dataAreas?.data?.map((elem: ExpenseAreaI) => {
                                            return (
                                                <div className="createTender__area area">
                                                    <div className="area__row">
                                                        <div className="area__title">
                                                            <div style={{ maxWidth: '300px' }}>
                                                                <EllipsisTooltipCell text={elem?.name} />
                                                            </div>
                                                            {elem?.form_status === 'no_documents' && (
                                                                <div
                                                                    className="area__no-documents"
                                                                    style={{ marginLeft: 8 }}
                                                                >
                                                                    No Documents
                                                                </div>
                                                            )}

                                                            <div className="area__dots">
                                                                {dots(elem.id, elem.status)}
                                                            </div>
                                                        </div>
                                                        <div
                                                            className={`area__checkbox ${
                                                                selectedEA === elem?.id
                                                                    ? 'selected'
                                                                    : elem.status === 'active'
                                                                    ? 'active'
                                                                    : 'disabled'
                                                            }`}
                                                            onClick={() => {
                                                                if (elem.status === 'active') {
                                                                    setSelectedEA(elem?.id);
                                                                }
                                                            }}
                                                        ></div>
                                                    </div>
                                                    <div className="EA components">
                                                        {elem?.components
                                                            ?.slice(0, 1)
                                                            .map((elem: ComponentExpenseArea, index: number) => (
                                                                <div className="EA__component without-icon" key={index}>
                                                                    {elem?.name}
                                                                </div>
                                                            ))}

                                                        {elem?.components && elem?.components.length > 1 && (
                                                            <div className="EA__group">
                                                                <div className="EA__component without-icon">
                                                                    {elem?.components[1].name}
                                                                </div>

                                                                {elem?.components.length > 2 && (
                                                                    <Popover
                                                                        trigger="click"
                                                                        placement="top"
                                                                        open={activePopoverIndex === elem?.id}
                                                                        onOpenChange={(visible) => {
                                                                            setActivePopoverIndex(
                                                                                visible ? elem?.id : null
                                                                            );
                                                                        }}
                                                                        content={
                                                                            <div
                                                                                className="EA__popover-content"
                                                                                onClick={(e) => e.stopPropagation()}
                                                                            >
                                                                                {elem?.components
                                                                                    .slice(2)
                                                                                    .map(
                                                                                        (
                                                                                            elem: ComponentExpenseArea,
                                                                                            index: number
                                                                                        ) => (
                                                                                            <div
                                                                                                key={index}
                                                                                                className="EA__component without-icon"
                                                                                            >
                                                                                                <span>{elem.name}</span>
                                                                                            </div>
                                                                                        )
                                                                                    )}
                                                                            </div>
                                                                        }
                                                                    >
                                                                        <div
                                                                            className={`EA__component without-icon ${
                                                                                activePopoverIndex === elem?.id
                                                                                    ? 'active'
                                                                                    : ''
                                                                            }`}
                                                                            onClick={(e) => e.stopPropagation()}
                                                                        >
                                                                            +{elem?.components.length - 2}
                                                                        </div>
                                                                    </Popover>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </Form>
                        </div>
                    </div>
                </Spin>
            </Modal>

            <Modal
                className="confirmModal"
                centered
                open={isConfirmModal}
                closeIcon={<div style={{ display: 'none', cursor: 'context-menu' }}></div>}
                width={600}
                footer={
                    <div style={{ display: 'flex', justifyItems: 'space-between', gap: '16px', width: '100%' }}>
                        <Button key="back" onClick={() => setIsConfirmModal(false)} block className="cancel">
                            Cancel
                        </Button>
                        <button
                            className="quit"
                            onClick={() => {
                                setOpenModal(false);
                                setIsConfirmModal(false);
                            }}
                        >
                            Quit
                        </button>
                    </div>
                }
            >
                <div className="modal-confirm">Your data won’t be saved! Are you sure you want to quit?</div>
            </Modal>
            {!!area?.id && (
                <ChangeStatusModal
                    id={area?.id}
                    setArea={setArea}
                    refetch={refetch}
                    type={area?.type}
                    AllEAs={dataAreas?.data}
                />
            )}
        </>
    );
};
